/**
 * Размеры уже нарисованной стены числом: длина в чистоте, толщина и высота. Одни и те же поля
 * в панели свойств справа и в разделе «Стены» слева, пока стена выделена: правят именно её,
 * а не параметры будущих стен. Каждая правка — свой шаг Ctrl+Z.
 */
import { UIKit } from "@/shared/ui";
import { useLengthUnit, useProjectStore } from "@/shared/store";
import { formatMillimeters } from "@/shared/lib/geometry";
import { useWallsStore } from "@/shared/store/layers";
import { wallFaceLengths, wallLength } from "@/widgets/plan";

export const WallSizeFields = ({ wallId }: { wallId: string }) => {
	const graph = useProjectStore((s) => s.project.layers.walls);
	const recordEdit = useProjectStore((s) => s.recordEdit);
	const updateWall = useWallsStore((s) => s.updateWall);
	const setWallLength = useWallsStore((s) => s.setWallLength);
	const unit = useLengthUnit();

	const wall = graph.walls[wallId];
	if (!wall) return null;
	// Длина — в чистоте, как на чертеже и как меряют рулеткой; ось стены длиннее
	// на полтолщины соседних стен с каждого конца, её пользователю показывать незачем
	const axis = wallLength(graph, wall);
	const faces = wallFaceLengths(graph, wall.id) ?? { clear: axis, outer: axis };

	return (
		<>
			<UIKit.NumberField
				label="Длина"
				value={Math.round(faces.clear)}
				min={100}
				step={50}
				// Вписали длину в чистоте — ось меняем на ту же разницу
				onChange={(clear) =>
					recordEdit(() => {
						setWallLength(wall.id, clear + (axis - faces.clear));
						// Соседняя стена могла наклониться, а длина в чистоте зависит от угла с ней:
						// уточняем вторым шагом, чтобы в чистоте вышло ровно вписанное
						const moved = useWallsStore.getState().items;
						const movedWall = moved.walls[wall.id];
						if (!movedWall) return;
						const movedAxis = wallLength(moved, movedWall);
						const movedClear = wallFaceLengths(moved, wall.id)?.clear ?? movedAxis;
						if (Math.abs(movedClear - clear) >= 1) {
							setWallLength(wall.id, movedAxis + (clear - movedClear));
						}
					})
				}
			/>
			<UIKit.Text className="text-xs text-ink">
				В чистоте, от угла до угла внутри комнаты; снаружи — {formatMillimeters(faces.outer, unit)}.
				Длина меняется сдвигом дальнего конца стены; соседняя стена в этом углу может встать
				наискось — угол между стенами задаётся отдельно, у выделенного угла.
			</UIKit.Text>
			<UIKit.NumberField
				label="Толщина"
				value={wall.thickness}
				min={40}
				max={800}
				step={10}
				onChange={(thickness) => recordEdit(() => updateWall(wall.id, { thickness }))}
			/>
			<UIKit.NumberField
				label="Высота"
				value={wall.height}
				min={2000}
				max={5000}
				step={50}
				onChange={(height) => recordEdit(() => updateWall(wall.id, { height }))}
			/>
		</>
	);
};
